<template>
  <div class="claim-screen-container w-full pb-4 sm:pb-8 space-y-4 sm:space-y-6">
    <!-- Top Header & Navigation Segment (New Claim vs History) -->
    <div class="flex items-center justify-between gap-3 pt-1">
      <!-- Vuetify Button Toggle for Views -->
      <v-btn-toggle v-model="activeView" mandatory borderless dense class="bg-slate-100 p-1 rounded-xl shrink-0">
        <v-btn value="form" small depressed :color="activeView === 'form' ? 'white' : 'transparent'"
          class="rounded-lg text-capitalize text-xs font-weight-bold"
          :class="activeView === 'form' ? 'blue--text text--darken-2 shadow-xs' : 'grey--text text--darken-2'">
          New Claim
        </v-btn>
        <v-btn value="history" small depressed :color="activeView === 'history' ? 'white' : 'transparent'"
          class="rounded-lg text-capitalize text-xs font-weight-bold flex items-center gap-1"
          :class="activeView === 'history' ? 'blue--text text--darken-2 shadow-xs' : 'grey--text text--darken-2'">
          <span>History</span>
          <v-chip v-if="userClaims.length" x-small color="blue lighten-5" text-color="blue darken-3"
            class="ml-1 px-1.5 font-weight-bold">
            {{ userClaims.length }}
          </v-chip>
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- ==================== VIEW 1: NEW CLAIM FORM (MATCHES SCREENSHOT) ==================== -->
    <div v-if="activeView === 'form'" class="space-y-4">
      <!-- 1. Category Options Horizontal Slider (Full Width, Smooth Native Touch & Drag Scrolling) -->
      <div class="category-slider-wrapper w-full">
        <div ref="sliderTrack"
          class="category-slider-track flex items-center gap-2.5 overflow-x-auto py-1 px-0.5 no-scrollbar select-none"
          @mousedown="handleSliderMouseDown" @mouseleave="handleSliderMouseLeave" @mouseup="handleSliderMouseUp"
          @mousemove="handleSliderMouseMove">
          <button v-for="(cat, idx) in categories" :key="cat.id" :ref="`catBtn_${idx}`" type="button"
            @click="selectCategory(idx)" :class="[
              'category-slide-btn flex flex-col items-center justify-center rounded-2xl h-[72px] min-h-[72px] border transition-all cursor-pointer select-none shrink-0',
              selectedCategoryIndex === idx
                ? 'text-white shadow-md active-cat border-transparent'
                : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-200'
            ]"
            :style="selectedCategoryIndex === idx ? { backgroundColor: $vuetify.theme.currentTheme.employeePrimary, borderColor: $vuetify.theme.currentTheme.employeePrimary } : {}">
            <v-icon size="24" :color="selectedCategoryIndex === idx ? 'white' : '#334155'"
              class="mb-1 pointer-events-none">
              {{ cat.icon }}
            </v-icon>
            <span
              class="text-[11px] sm:text-xs font-semibold tracking-tight truncate w-full text-center px-1 pointer-events-none"
              :class="selectedCategoryIndex === idx ? 'text-white font-bold' : 'text-slate-700'">
              {{ cat.label }}
            </span>
          </button>
        </div>
      </div>

      <!-- 2. Metric Summary Card (Total Limit, Claimed, Remaining Balance) -->
      <div class="bg-[#f0f7ff] border border-[#bae0ff] rounded-2xl p-3.5 sm:p-4 shadow-2xs">
        <div class="grid grid-cols-3 divide-x divide-[#cbe4ff]">
          <!-- Total Limit -->
          <div class="flex flex-col items-center justify-center px-1 text-center">
            <div class="text-[#1677ff] mb-1">
              <!-- Stacked Coins Icon (Exact 3 disks) -->
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 3.34 2 5s4.48 3 10 3 10-1.34 10-3-4.48-3-10-3z" />
                <path d="M2 7.5v3c0 1.66 4.48 3 10 3s10-1.34 10-3v-3c-1.8 1.13-5.57 1.8-10 1.8S3.8 8.63 2 7.5z" />
                <path d="M2 13.5v3c0 1.66 4.48 3 10 3s10-1.34 10-3v-3c-1.8 1.13-5.57 1.8-10 1.8S3.8 14.63 2 13.5z" />
              </svg>
            </div>
            <span class="text-[11px] sm:text-xs font-medium text-slate-600 block leading-tight">
              Total Limit
            </span>
            <span class="text-base sm:text-lg font-black text-slate-900 mt-0.5 tracking-tight">
              ₹{{ formatNumber(currentTotalLimit) }}
            </span>
          </div>

          <!-- Claimed -->
          <div class="flex flex-col items-center justify-center px-1 text-center">
            <div class="text-[#1677ff] mb-1">
              <!-- Wallet / Card Icon -->
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M21 18v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v1h-9a2 2 0 00-2 2v8a2 2 0 002 2h9zm-9-2h10V8H12v8zm4-2.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
              </svg>
            </div>
            <span class="text-[11px] sm:text-xs font-medium text-slate-600 block leading-tight">
              Claimed
            </span>
            <span class="text-base sm:text-lg font-black text-slate-900 mt-0.5 tracking-tight">
              ₹{{ formatNumber(currentClaimedAmount) }}
            </span>
          </div>

          <!-- Remaining Balance -->
          <div class="flex flex-col items-center justify-center px-1 text-center">
            <div class="text-[#1677ff] mb-1">
              <!-- Remaining Wallet Icon -->
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M19 5H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2zm-1 9h-3a2 2 0 01-2-2 2 2 0 012-2h3v4z" />
              </svg>
            </div>
            <span class="text-[11px] sm:text-xs font-medium text-slate-600 block leading-tight">
              Remaining Balance
            </span>
            <span class="text-base sm:text-lg font-black text-slate-900 mt-0.5 tracking-tight">
              ₹{{ formatNumber(currentRemainingBalance) }}
            </span>
          </div>
        </div>
      </div>

      <!-- 3. Claim Submission Form Card with Vuetify Form Fields -->
      <div class="bg-white rounded-2xl border border-blue-100 shadow-2xs p-4 sm:p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Vuetify Select: Month -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              <span class="text-red-500 font-bold">*</span> Month
            </label>
            <v-select v-model="formState.month" :items="monthOptions" item-text="label" item-value="value" outlined dense
              hide-details="auto" append-icon="mdi-chevron-down" placeholder="Select Month" class="custom-vuetify-input"
              :menu-props="{ rounded: 'xl', offsetY: true, contentClass: 'elevation-3' }" />
          </div>

          <!-- Vuetify Text Field: Bill Number -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              <span class="text-red-500 font-bold">*</span> Bill Number
            </label>
            <v-text-field v-model.trim="formState.billNumber" placeholder="Enter bill number" outlined dense
              hide-details="auto" class="custom-vuetify-input" />
          </div>

          <!-- Vuetify Menu & Date Picker: Bill Date -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              <span class="text-red-500 font-bold">*</span> Bill Date
            </label>
            <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="formState.billDate" placeholder="Select date"
                  append-icon="mdi-calendar-blank-outline" readonly outlined dense hide-details="auto" v-bind="attrs"
                  v-on="on" class="custom-vuetify-input cursor-pointer" />
              </template>
              <v-date-picker v-model="formState.billDate" :color="$vuetify.theme.currentTheme.employeePrimary" @input="dateMenu = false"></v-date-picker>
            </v-menu>
          </div>

          <!-- Vuetify Text Field: Claim Amount -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              <span class="text-red-500 font-bold">*</span> Claim Amount
            </label>
            <v-text-field v-model.number="formState.amount" type="number" min="1" prefix="₹"
              placeholder="Enter claim amount" outlined dense hide-details="auto" class="custom-vuetify-input" />
            <span v-if="formState.amount && formState.amount > currentRemainingBalance"
              class="text-[11px] text-amber-600 font-medium mt-1 block">
              Notice: This amount exceeds your remaining balance (₹{{ formatNumber(currentRemainingBalance) }}).
            </span>
          </div>

          <!-- Vuetify Textarea: Comment (Optional) -->
          <div class="md:col-span-2">
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              Comment
            </label>
            <v-textarea v-model.trim="formState.comment" rows="3" counter="250"
              placeholder="Add any additional information..." outlined dense hide-details="auto"
              class="custom-vuetify-input" />
          </div>

          <!-- Attachments Field -->
          <div class="md:col-span-2">
          <label class="block text-xs font-semibold text-slate-800 mb-1.5">
            <span class="text-red-500 font-bold">*</span> Attachments
          </label>

          <!-- Hidden Native File Input -->
          <input type="file" ref="fileInputRef" class="hidden" accept=".pdf,.jpg,.jpeg,.png"
            @change="handleFileChange" />

          <!-- Upload Dropzone (When no file selected) -->
          <div v-if="!selectedFile" @click="triggerFileInput"
            class="border-2 border-dashed border-[#bae0ff] hover:border-[#1677ff] rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-blue-50/40 transition-colors bg-white select-none">
            <!-- Cloud Upload Icon (Blue cloud with upward arrow) -->
            <div class="w-10 h-10 rounded-full bg-blue-50 text-[#1677ff] flex items-center justify-center mb-1.5">
              <v-icon color="#1677ff" size="28">mdi-cloud-upload-outline</v-icon>
            </div>
            <span class="text-xs sm:text-sm font-bold text-[#1677ff]">
              Upload receipt / invoice
            </span>
            <span class="text-[11px] text-slate-400 mt-0.5 font-medium">
              PDF, JPG, PNG (Max 5MB)
            </span>
          </div>

          <!-- Uploaded File Badge / Preview Card -->
          <div v-else
            class="border border-blue-200 bg-blue-50/50 rounded-xl p-3 flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              <!-- Thumbnail or Document Icon -->
              <div
                class="w-10 h-10 rounded-lg bg-white border border-blue-100 flex items-center justify-center shrink-0 overflow-hidden">
                <img v-if="filePreviewUrl" :src="filePreviewUrl" alt="Receipt Preview"
                  class="w-full h-full object-cover" />
                <v-icon v-else color="red darken-1">mdi-file-pdf-box</v-icon>
              </div>

              <!-- File Name & Size -->
              <div class="min-w-0">
                <p class="text-xs font-bold text-slate-800 truncate mb-0.5">
                  {{ selectedFile.name }}
                </p>
                <span class="text-[11px] text-slate-500">
                  {{ formatFileSize(selectedFile.size) }} • Ready
                </span>
              </div>
            </div>

            <!-- Remove Button -->
            <v-btn icon small color="red" @click.stop="removeSelectedFile" class="hover:bg-red-50 shrink-0"
              title="Remove Attachment">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        </div>
      </div>

      <!-- 4. Vuetify Submit Claim Full-Width Button -->
      <v-btn block depressed large :color="$vuetify.theme.currentTheme.employeePrimary" :loading="isSubmitting" :disabled="isSubmitting"
        @click="handleSubmitClaim"
        class="rounded-xl white--text font-weight-bold text-capitalize text-sm sm:text-base h-12 shadow-xs">
        Submit Claim
      </v-btn>
    </div>

    <!-- ==================== VIEW 2: CLAIM HISTORY ==================== -->
    <div v-else class="space-y-4">
      <!-- Vuetify Status Filter Chips -->
      <div class="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        <v-chip v-for="st in statusFilters" :key="st.value" filter small :outlined="selectedStatusFilter !== st.value"
          :color="selectedStatusFilter === st.value ? $vuetify.theme.currentTheme.employeePrimary : 'grey lighten-2'"
          :text-color="selectedStatusFilter === st.value ? 'white' : 'grey darken-3'"
          @click="selectedStatusFilter = st.value" class="font-weight-medium text-xs cursor-pointer">
          {{ st.label }}
        </v-chip>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingClaims" class="py-12 flex flex-col items-center justify-center">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        <span class="text-xs text-slate-500 font-medium mt-2">Loading claim records...</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="!filteredClaims.length" class="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <div class="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3">
          <v-icon color="#1677ff" size="30">mdi-clipboard-text-clock-outline</v-icon>
        </div>
        <h3 class="text-sm font-bold text-slate-800 mb-1">No Claims Found</h3>
        <p class="text-xs text-slate-500 mb-4 max-w-xs mx-auto">
          {{ emptyStateMessage }}
        </p>
        <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-xl font-weight-bold text-capitalize"
          @click="activeView = 'form'">
          Submit Claim
        </v-btn>
      </div>

      <!-- Claims List -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
        <div v-for="item in filteredClaims" :key="item._id || item.id"
          class="bg-white rounded-2xl border border-slate-200 p-4 shadow-2xs hover:border-blue-200 transition-all flex flex-col justify-between space-y-3">
          <!-- Top Row: Category, Bill # and Status Badge -->
          <div class="flex items-start justify-between gap-2">
            <div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-bold text-slate-900">
                  {{ getCategoryLabel(item.reimbursementType) }}
                </span>
                <span v-if="item.billNumber"
                  class="text-[11px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                  #{{ item.billNumber }}
                </span>
              </div>
              <span class="text-[11px] text-slate-400 block mt-0.5">
                {{ formatDateTime(item.createdAt) }}
              </span>
            </div>

            <!-- Status Pill -->
            <span class="px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wide uppercase border"
              :class="getStatusBadgeClass(getClaimStatus(item))">
              {{ getClaimStatus(item) }}
            </span>
          </div>

          <!-- Amount & Month Grid -->
          <div class="grid grid-cols-2 gap-2 bg-slate-50 rounded-xl p-2.5 text-xs">
            <div>
              <span class="text-slate-400 text-[10px] block">Claimed Amount</span>
              <span class="font-extrabold text-slate-900 text-sm">
                ₹{{ formatNumber(item.claimAmount) }}
              </span>
            </div>
            <div>
              <span class="text-slate-400 text-[10px] block">For Month / Date</span>
              <span class="font-semibold text-slate-700">
                {{ item.month || 'N/A' }} {{ item.year ? `(${item.year})` : '' }}
              </span>
            </div>
          </div>

          <!-- Bill Date & Comments if any -->
          <div v-if="item.comments && item.comments.length"
            class="text-xs text-slate-600 bg-blue-50/50 rounded-lg p-2 border border-blue-100">
            <span class="font-bold text-slate-700 block text-[11px] mb-0.5">Note:</span>
            <p class="mb-0 text-slate-600 line-clamp-2">
              {{ item.comments[item.comments.length - 1].message }}
            </p>
          </div>

          <!-- Actions Row: View Attachment, Edit / Resubmit -->
          <div class="flex items-center justify-between pt-1 border-t border-slate-100 text-xs">
            <a v-if="item.attachedBill" :href="getAttachmentFullUrl(item.attachedBill)" target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 no-underline">
              <v-icon small color="primary">mdi-link-variant</v-icon>
              <span>View Receipt</span>
            </a>
            <span v-else class="text-slate-400 text-[11px]">No receipt attached</span>

            <!-- Edit/Resubmit button if Rejected or Pending -->
            <v-btn v-if="canEditClaim(item)" text x-small :color="$vuetify.theme.currentTheme.employeePrimary"
              class="font-weight-bold rounded-lg text-capitalize" @click="openEditClaimModal(item)">
              Edit Claim
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Claim Modal Dialog (Vuetify Dialog) -->
    <v-dialog v-model="editModalOpen" max-width="480">
      <v-card class="rounded-3xl pa-5 shadow-xl">
        <div class="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
          <h3 class="text-base font-extrabold text-slate-900 mb-0">
            Edit / Resubmit Claim
          </h3>
          <v-btn icon small @click="editModalOpen = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="editingClaim" class="space-y-3.5">
          <!-- Bill Number -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Bill Number</label>
            <v-text-field v-model.trim="editFormState.billNumber" outlined dense hide-details="auto"
              class="custom-vuetify-input" />
          </div>

          <!-- Bill Date -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Bill Date</label>
            <v-menu v-model="editDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="editFormState.billDate" placeholder="Select date"
                  append-icon="mdi-calendar-blank-outline" readonly outlined dense hide-details="auto" v-bind="attrs"
                  v-on="on" class="custom-vuetify-input cursor-pointer" />
              </template>
              <v-date-picker v-model="editFormState.billDate" :color="$vuetify.theme.currentTheme.employeePrimary"
                @input="editDateMenu = false"></v-date-picker>
            </v-menu>
          </div>

          <!-- Claim Amount -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Claim Amount (₹)</label>
            <v-text-field v-model.number="editFormState.amount" type="number" prefix="₹" min="1" outlined dense
              hide-details="auto" class="custom-vuetify-input" />
          </div>

          <!-- Comment -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Reason / Update Comment</label>
            <v-textarea v-model.trim="editFormState.comment" rows="2" outlined dense hide-details="auto"
              placeholder="Explain any change or clarification..." class="custom-vuetify-input" />
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-2 pt-3">
            <v-btn text small class="rounded-xl text-capitalize font-weight-bold" @click="editModalOpen = false">
              Cancel
            </v-btn>
            <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" :loading="isUpdating" :disabled="isUpdating"
              class="white--text rounded-xl text-capitalize font-weight-bold" @click="handleSaveEditedClaim">
              Update & Resubmit
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  getCustomReimbursements,
  getUserReimbursements,
  uploadReimbursementDocument,
  submitReimbursement,
  updateReimbursement
} from '@/app/services/claim'

export default {
  name: 'AppClaim',
  data() {
    return {
      activeView: 'form', // 'form' | 'history'
      isLoadingConfig: false,
      isLoadingClaims: false,
      isSubmitting: false,
      isUpdating: false,
      dateMenu: false,
      editDateMenu: false,

      isDraggingSlider: false,
      sliderStartX: 0,
      sliderScrollLeft: 0,

      // Categories Tab selection index (0 = Phone, 1 = Internet, 2 = Travel, 3 = Misc)
      selectedCategoryIndex: 0,
      categories: [
        { id: 'phone', label: 'Phone', type: 'Phone', icon: 'mdi-phone', limit: 6000, _id: null },
        { id: 'internet', label: 'Internet', type: 'Internet', icon: 'mdi-wifi', limit: 6000, _id: null },
        { id: 'travel', label: 'Travel', type: 'Travel', icon: 'mdi-airplane', limit: 6000, _id: null },
        { id: 'misc', label: 'Misc', type: 'Miscellaneous', icon: 'mdi-dots-horizontal', limit: 6000, _id: null }
      ],

      // Form State
      formState: {
        month: 8, // 0-indexed: 8 is September
        billNumber: '',
        billDate: dayjs().format('YYYY-MM-DD'),
        amount: null,
        comment: ''
      },

      // File Upload State
      selectedFile: null,
      filePreviewUrl: null,

      // Claims Data
      userClaims: [],
      selectedStatusFilter: 'ALL',
      statusFilters: [
        { label: 'All', value: 'ALL' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' },
        { label: 'Resubmitted', value: 'RESUBMITTED' }
      ],

      // Edit Claim Modal State
      editModalOpen: false,
      editingClaim: null,
      editFormState: {
        billNumber: '',
        billDate: '',
        amount: null,
        comment: ''
      }
    }
  },
  computed: {
    selectedCategory() {
      return this.categories[this.selectedCategoryIndex] || this.categories[0]
    },
    currentUser() {
      return this.$storage?.getUniversal('user') || JSON.parse(localStorage.getItem('user')) || {}
    },
    currentUserId() {
      return this.currentUser?._id || this.currentUser?.user?._id || ''
    },
    s3BaseUrl() {
      return process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/'
    },
    monthOptions() {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      const currentYear = dayjs().year()
      const currentMonth = dayjs().month() // 0-11
      const startYear = currentMonth >= 3 ? currentYear : currentYear - 1

      // Standard Indian Financial Year order: Apr (3) to Mar (2)
      const fyOrder = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2]
      return fyOrder.map((monthIdx) => {
        const year = monthIdx >= 3 ? startYear : startYear + 1
        return {
          label: `${monthNames[monthIdx]} ${year}`,
          monthName: monthNames[monthIdx],
          year,
          value: monthIdx
        }
      })
    },
    currentTotalLimit() {
      return this.selectedCategory?.limit || 6000
    },
    currentClaimedAmount() {
      if (!this.userClaims || !this.userClaims.length || !this.selectedCategory) return 0
      const selectedMonthObj = this.monthOptions.find((m) => m.value === this.formState.month)
      const selectedMonthName = selectedMonthObj?.monthName?.toLowerCase()

      // Calculate sum of approved or submitted claims for this category & month
      return this.userClaims.reduce((sum, item) => {
        const catMatch =
          item.reimbursementType === this.selectedCategory._id ||
          item.reimbursementType === this.selectedCategory.id ||
          (item.reimbursementTypeName &&
            (item.reimbursementTypeName.toLowerCase() === this.selectedCategory.label.toLowerCase() ||
              (this.selectedCategory.type && item.reimbursementTypeName.toLowerCase() === this.selectedCategory.type.toLowerCase()) ||
              (this.selectedCategory.id === 'misc' && item.reimbursementTypeName.toLowerCase().startsWith('mis'))))

        const monthMatch =
          !selectedMonthName ||
          (item.month && item.month.toLowerCase().includes(selectedMonthName))

        if (catMatch && monthMatch) {
          return sum + (Number(item.claimAmount) || 0)
        }
        return sum
      }, 0)
    },
    currentRemainingBalance() {
      return Math.max(0, this.currentTotalLimit - this.currentClaimedAmount)
    },
    filteredClaims() {
      if (!this.userClaims) return []
      if (this.selectedStatusFilter === 'ALL') return this.userClaims
      return this.userClaims.filter((c) => {
        const status = this.getClaimStatus(c).toUpperCase()
        return status === this.selectedStatusFilter
      })
    },
    emptyStateMessage() {
      if (this.selectedStatusFilter !== 'ALL') {
        return `No claims with status "${this.selectedStatusFilter}".`
      }
      return 'You have not submitted any corporate expense claims yet.'
    }
  },
  created() {
    this.initMonthDefault()
    this.fetchReimbursementConfig()
    this.fetchUserClaims()
  },
  methods: {
    initMonthDefault() {
      // By default select September 2026 or current month if available
      const septOption = this.monthOptions.find((m) => m.value === 8)
      if (septOption) {
        this.formState.month = 8 // September
      } else {
        this.formState.month = dayjs().month()
      }
    },
    selectCategory(idx) {
      this.selectedCategoryIndex = idx
      this.$nextTick(() => {
        const btn = this.$refs[`catBtn_${idx}`]?.[0] || this.$refs[`catBtn_${idx}`]
        if (btn && btn.scrollIntoView) {
          btn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
        }
      })
    },
    handleSliderMouseDown(e) {
      if (!this.$refs.sliderTrack) return
      this.isDraggingSlider = true
      this.sliderStartX = e.pageX - this.$refs.sliderTrack.offsetLeft
      this.sliderScrollLeft = this.$refs.sliderTrack.scrollLeft
    },
    handleSliderMouseLeave() {
      this.isDraggingSlider = false
    },
    handleSliderMouseUp() {
      this.isDraggingSlider = false
    },
    handleSliderMouseMove(e) {
      if (!this.isDraggingSlider || !this.$refs.sliderTrack) return
      e.preventDefault()
      const x = e.pageX - this.$refs.sliderTrack.offsetLeft
      const walk = (x - this.sliderStartX) * 1.5
      this.$refs.sliderTrack.scrollLeft = this.sliderScrollLeft - walk
    },
    formatNumber(num) {
      const val = Number(num) || 0
      return new Intl.NumberFormat('en-IN').format(val)
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      return dayjs(dateStr).format('DD MMM, YYYY')
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
    triggerFileInput() {
      if (this.$refs.fileInputRef) {
        this.$refs.fileInputRef.click()
      }
    },
    handleFileChange(e) {
      const file = e.target.files?.[0]
      if (!file) return

      // Validate size (max 5MB)
      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        this.$toast?.error('File size exceeds the 5MB limit. Please choose a smaller file.')
        return
      }

      this.selectedFile = file

      // Generate preview if image
      if (file.type.startsWith('image/')) {
        this.filePreviewUrl = URL.createObjectURL(file)
      } else {
        this.filePreviewUrl = null
      }
    },
    removeSelectedFile() {
      this.selectedFile = null
      if (this.filePreviewUrl) {
        URL.revokeObjectURL(this.filePreviewUrl)
        this.filePreviewUrl = null
      }
      if (this.$refs.fileInputRef) {
        this.$refs.fileInputRef.value = ''
      }
    },
    async fetchReimbursementConfig() {
      this.isLoadingConfig = true
      try {
        const res = await getCustomReimbursements()
        const customItems = res.data?.customReimbursements || []

        if (customItems.length > 0) {
          // Merge API configurations with categories
          customItems.forEach((apiItem) => {
            const apiType = (apiItem.type || '').trim()
            const existingCat = this.categories.find(
              (c) =>
                c.label.toLowerCase() === apiType.toLowerCase() ||
                (c.type && c.type.toLowerCase() === apiType.toLowerCase()) ||
                (apiType.toLowerCase().startsWith('mis') && c.id === 'misc')
            )

            if (existingCat) {
              existingCat._id = apiItem._id
              if (apiItem.monthlyAllowedLimit) {
                existingCat.limit = apiItem.monthlyAllowedLimit
              }
            } else {
              // Add new custom category from backend
              let icon = 'mdi-dots-horizontal'
              const lower = apiType.toLowerCase()
              if (lower.includes('phone') || lower.includes('mobile')) icon = 'mdi-phone'
              else if (lower.includes('internet') || lower.includes('wifi')) icon = 'mdi-wifi'
              else if (lower.includes('travel') || lower.includes('flight')) icon = 'mdi-airplane'

              this.categories.push({
                id: apiItem._id,
                _id: apiItem._id,
                label: apiType,
                type: apiType,
                icon,
                limit: apiItem.monthlyAllowedLimit || 6000
              })
            }
          })
        }
      } catch (err) {
        // Fallbacks remain intact
        console.warn('Failed to load custom reimbursements, using default limits:', err)
      } finally {
        this.isLoadingConfig = false
      }
    },
    async fetchUserClaims() {
      if (!this.currentUserId) return
      this.isLoadingClaims = true
      try {
        const res = await getUserReimbursements(this.currentUserId, {
          year: dayjs().year()
        })
        this.userClaims = res.data?.reimbursements || []
      } catch (err) {
        console.warn('Failed to load user claims:', err)
      } finally {
        this.isLoadingClaims = false
      }
    },
    getClaimStatus(claim) {
      if (!claim) return 'PENDING'
      if (claim.approvals && claim.approvals.length > 0) {
        const last = claim.approvals[claim.approvals.length - 1]
        return (last.status || 'Pending').toUpperCase()
      }
      return (claim.status || 'Pending').toUpperCase()
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'APPROVED':
          return 'bg-emerald-50 text-emerald-700 border-emerald-200'
        case 'REJECTED':
          return 'bg-rose-50 text-rose-700 border-rose-200'
        case 'RESUBMITTED':
          return 'bg-sky-50 text-sky-700 border-sky-200'
        case 'PENDING':
        default:
          return 'bg-amber-50 text-amber-700 border-amber-200'
      }
    },
    getCategoryLabel(typeId) {
      if (!typeId) return 'Expense Claim'
      const found = this.categories.find((c) => c._id === typeId || c.id === typeId)
      return found ? found.label : 'Expense Claim'
    },
    getAttachmentFullUrl(path) {
      if (!path) return '#'
      if (path.startsWith('http://') || path.startsWith('https://')) return path
      return `${this.s3BaseUrl}${path}`
    },
    canEditClaim(claim) {
      const status = this.getClaimStatus(claim)
      return status === 'PENDING' || status === 'REJECTED' || status === 'RESUBMITTED'
    },
    openEditClaimModal(claim) {
      this.editingClaim = claim
      this.editFormState.billNumber = claim.billNumber || ''
      this.editFormState.billDate = claim.billDate ? dayjs(claim.billDate).format('YYYY-MM-DD') : ''
      this.editFormState.amount = claim.claimAmount || null
      const lastComment = claim.comments?.[claim.comments.length - 1]
      this.editFormState.comment = lastComment?.message || ''
      this.editModalOpen = true
    },
    async handleSaveEditedClaim() {
      if (!this.editingClaim) return
      if (!this.editFormState.amount || this.editFormState.amount <= 0) {
        this.$toast?.error('Please enter a valid claim amount.')
        return
      }

      this.isUpdating = true
      try {
        const payload = {
          billNumber: this.editFormState.billNumber,
          billDate: this.editFormState.billDate,
          claimAmount: Number(this.editFormState.amount),
          approvals: [...(this.editingClaim.approvals || []), { status: 'Resubmitted' }],
          comments: [
            ...(this.editingClaim.comments || []),
            {
              message: this.editFormState.comment || 'Resubmitted by employee',
              commentedBy: this.currentUserId,
              commentedAt: new Date().toISOString()
            }
          ]
        }

        const id = this.editingClaim._id || this.editingClaim.id
        await updateReimbursement(id, payload)
        this.$toast?.success('Claim resubmitted successfully!')
        this.editModalOpen = false
        this.fetchUserClaims()
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.message || 'Failed to update claim.'
        this.$toast?.error(errorMsg)
      } finally {
        this.isUpdating = false
      }
    },
    async handleSubmitClaim() {
      // 1. Validation
      if (!this.formState.billNumber) {
        this.$toast?.error('Please enter the bill number.')
        return
      }
      if (!this.formState.billDate) {
        this.$toast?.error('Please select the bill date.')
        return
      }
      if (!this.formState.amount || Number(this.formState.amount) <= 0) {
        this.$toast?.error('Please enter a valid claim amount.')
        return
      }
      if (!this.selectedFile) {
        this.$toast?.error('Please attach a bill / receipt invoice.')
        return
      }

      this.isSubmitting = true
      try {
        const selectedMonthObj = this.monthOptions.find((m) => m.value === this.formState.month)
        const monthName = selectedMonthObj?.monthName || 'September'
        const year = selectedMonthObj?.year || dayjs().year()

        // 2. Upload Document First
        let attachedDocPath = null
        if (this.selectedFile) {
          const uploadFormData = new FormData()
          uploadFormData.append('attachments', this.selectedFile)
          uploadFormData.append('month', monthName.toLowerCase())
          if (this.currentUserId) {
            uploadFormData.append('userId', this.currentUserId)
          }

          const uploadRes = await uploadReimbursementDocument(uploadFormData)
          if (uploadRes.data?.success && uploadRes.data?.url?.doc_physical_path) {
            attachedDocPath = uploadRes.data.url.doc_physical_path
          } else if (uploadRes.data?.url) {
            attachedDocPath = typeof uploadRes.data.url === 'string' ? uploadRes.data.url : uploadRes.data.url.doc_physical_path
          } else {
            throw new Error('Failed to upload receipt attachment.')
          }
        }

        // 3. Build Submission Payload
        const payload = {
          user: this.currentUserId,
          reimbursementType: this.selectedCategory._id || this.selectedCategory.id,
          reimbursementTypeName: this.selectedCategory.label,
          claimAmount: Number(this.formState.amount),
          month: monthName,
          year: year,
          billNumber: this.formState.billNumber,
          billDate: this.formState.billDate,
          attachedBill: attachedDocPath,
          comments: [
            {
              message: this.formState.comment ? this.formState.comment.trim() : 'Submitted via employee portal',
              commentedBy: this.currentUserId,
              commentedAt: new Date().toISOString()
            }
          ],
          approvals: [
            {
              status: 'Pending'
            }
          ]
        }

        // 4. Submit via API
        await submitReimbursement(payload)
        this.$toast?.success('Your claim has been submitted successfully!')

        // 5. Reset Form
        this.formState.billNumber = ''
        this.formState.amount = null
        this.formState.comment = ''
        this.removeSelectedFile()

        // 6. Refresh claims data and switch to history
        await this.fetchUserClaims()
        this.activeView = 'history'
      } catch (err) {
        console.error('Claim submission error:', err)
        const errorMsg = err.response?.data?.message || err.message || 'Failed to submit claim.'
        this.$toast?.error(errorMsg)
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
/* Category Options Slider Custom Styling */
.category-slider-wrapper {
  width: 100%;
}

.category-slider-track {
  width: 100%;
  display: flex;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  cursor: grab;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.category-slider-track:active {
  cursor: grabbing;
}

.category-slider-track::-webkit-scrollbar {
  display: none;
}

.category-slide-btn {
  flex: 1 1 0px;
  min-width: 80px;
  padding: 8px 6px;
  outline: none !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -webkit-tap-highlight-color: transparent !important;
}

.category-slide-btn:focus {
  outline: none !important;
}

/* Vuetify Field Overrides to match precise rounded, clean aesthetic */
.custom-vuetify-input.v-text-field--outlined>>>fieldset {
  border-radius: 12px !important;
  border-color: #cbd5e1 !important;
  transition: border-color 0.2s ease;
}

.custom-vuetify-input.v-text-field--outlined.v-input--is-focused>>>fieldset {
  border-color: #1677ff !important;
  border-width: 1.5px !important;
}

.custom-vuetify-input>>>.v-input__control {
  min-height: 42px !important;
}

.custom-vuetify-input>>>.v-input__slot {
  min-height: 42px !important;
  padding: 0 12px !important;
  background-color: white !important;
}

.custom-vuetify-input>>>input,
.custom-vuetify-input>>>textarea {
  color: #1e293b !important;
  font-size: 14px !important;
}

.custom-vuetify-input>>>.v-text-field__prefix {
  font-weight: 600 !important;
  color: #334155 !important;
  margin-top: 0 !important;
}

/* Hide scrollbars for chips */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
